// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import {
  PlayerAttributesRow,
  PlayerRow,
} from "@suns/api/generated-client/apollo";
import bigBoards from "../big-board-ranks.json";
import { Flex, Text } from "@suns/design-system";
import { colors } from "@/shared/utils/colors";
import {
  ApolloAnalyticsIcon,
  ApolloBadgeIcon,
  ApolloScoutIcon,
  Icon,
} from "@suns/design-system/icons";

const sunsDnaCount = (attributes: PlayerAttributesRow[] | null) => {
  return attributes?.reduce<number>((acc, attribute) => {
    if (!attribute.value) {
      return acc;
    }

    let toAdd = 0;
    switch (attribute.key) {
      case PlayerAttributesRow.key.COMMUNICATES:
      case PlayerAttributesRow.key.IMPROVEMENT_DRIVEN:
      case PlayerAttributesRow.key.PHYSICALLY_TOUGH:
      case PlayerAttributesRow.key.PLAYS_HARD:
      case PlayerAttributesRow.key.PLAYS_WITH_PASSION_AND_EMOTIONS:
      case PlayerAttributesRow.key.POSITIVE_ENERGY:
      case PlayerAttributesRow.key.TEAM_FIRST_MINDSET:
      case PlayerAttributesRow.key.GETS_EXTRA_POSSESSIONS:
        toAdd = 1;
        break;
      case PlayerAttributesRow.key.IMPACTS_WINNING:
        toAdd = 2;
        break;
    }
    return acc + toAdd;
  }, 0);
};

export default function PlayerRanks({
  player,
  size = "md",
}: {
  player: PlayerRow;
  size?: "sm" | "md";
}) {
  const bigBoardRank =
    bigBoards.scoutBigBoard.findIndex(
      (rankedPlayer) => rankedPlayer.playerId === player.id
    ) + 1;
  const bigBoardRankTotal = bigBoards.scoutBigBoard.length;

  const analyticRank =
    bigBoards.analyticsBigBoard.find(
      (rankedPlayer) => rankedPlayer.playerId === player.id
    )?.rank ?? undefined;
  const analyticRankTotal =
    bigBoards.analyticsBigBoard[bigBoards.analyticsBigBoard.length - 1]?.rank ??
    undefined;

  const consensusPlayer = bigBoards.consensusBigBoard.find(
    (rankedPlayer) => rankedPlayer.playerId === player.id
  );

  const sunsDna = sunsDnaCount(player.metadata[0]?.attributes ?? null);

  return (
    <Flex
      direction="right"
      gap={size === "sm" ? "sm" : "md"}
      align="center"
      className="mt-1 max-sm:gap-1 md:mr-8"
      wrap
    >
      {player.metadata[0]?.rank && (
        <RankBadge
          label="Rank"
          value={player.metadata[0]?.rank ?? undefined}
          total={player.metadata[0]?.rankTotal ?? undefined}
          size={size}
          color={colors.bugambilia}
          Icon={ApolloAnalyticsIcon}
        />
      )}
      {player.metadata[0]?.positionRank && (
        <RankBadge
          label={
            player.metadata[0]?.hybridPosition
              ? `${player.metadata[0]?.hybridPosition.replace("_", " ").toLowerCase()}s Rank`
              : "Rank"
          }
          value={player.metadata[0]?.positionRank ?? undefined}
          total={player.metadata[0]?.positionRankTotal ?? undefined}
          size={size}
          color={colors.bugambilia}
          Icon={ApolloAnalyticsIcon}
        />
      )}
      {bigBoardRank ? (
        <RankBadge
          label="Big Board"
          value={bigBoardRank}
          total={bigBoardRankTotal}
          size={size}
          color={colors.xihuitl}
          Icon={ApolloScoutIcon}
        />
      ) : null}
      {bigBoardRank ? (
        <RankBadge
          label="Analytic"
          value={analyticRank ?? "--"}
          total={analyticRankTotal}
          color={colors.bugambilia}
          size={size}
          Icon={ApolloAnalyticsIcon}
        />
      ) : null}
      {bigBoardRank ? (
        <RankBadge
          label="Consensus"
          value={
            consensusPlayer
              ? consensusPlayer.min === consensusPlayer.max
                ? consensusPlayer.min
                : `${consensusPlayer.min}-${consensusPlayer.max}`
              : "--"
          }
          size={size}
          total={60}
          Icon={ApolloBadgeIcon}
        />
      ) : null}
      {sunsDna ? (
        <RankBadge
          label="DNA Score"
          value={sunsDna}
          total={10}
          color={colors.xihuitl}
          size={size}
          Icon={ApolloScoutIcon}
        />
      ) : null}
    </Flex>
  );
}

function RankBadge({
  value,
  total,
  label,
  color = colors.purple,
  size = "md",
  Icon,
}: {
  value?: number | string;
  total?: number;
  label: string;
  color?: string;
  size?: "sm" | "md";
  Icon?: Icon;
}) {
  return (
    <Flex direction="down" gap="sm" className="max-sm:min-w-[80px]">
      <Flex direction="right" gap="xs" align="center">
        {Icon && (
          <Icon
            size={size === "md" ? 20 : 16}
            color={color}
            className="max-sm:hidden"
          />
        )}
        <Text
          size={size === "sm" ? "2xs" : "sm"}
          heading
          muted
          className="capitalize max-sm:hidden"
        >
          {label}
        </Text>
        <Text
          size={size === "sm" ? "xs" : "sm"}
          heading
          muted
          className="capitalize max-sm:text-xs md:hidden"
        >
          {label === "point guards Rank" ? "PG Rank" : label}
        </Text>
      </Flex>
      <Flex direction="right" gap="none" align="start">
        <Text
          size={size === "sm" ? "lg" : "2xl"}
          heading
          className="-mt-1 max-sm:text-lg"
        >
          {value ?? "--"}
        </Text>
        <Text
          size={size === "sm" ? "xs" : "sm"}
          heading
          muted
          className="max-sm:text-xs"
        >
          /{total ?? "--"}
        </Text>
      </Flex>
    </Flex>
  );
}
